import React from 'react';
import styled from 'styled-components';
import { Button } from 'react-bootstrap';
import { theme } from '../../styles/theme';

type ButtonVariant = 'yellow' | 'primary' | 'green' | 'default';

interface BaseButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  fullWidth?: boolean;
  icon?: React.ReactNode;
}

const StyledButton = styled(Button)<{
  $variant?: ButtonVariant;
  $fullWidth?: boolean;
}>`
  position: relative;
  font-weight: 600;
  font-family: ${theme.fonts.secondary};
  font-size: 0.9375rem;
  padding: 0.875rem 1.5rem;
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
  }

  /* Shine effect on hover */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }

  ${props => {
    switch (props.$variant) {
      case 'yellow':
        return `
          background: ${theme.gradients.gold};
          color: ${theme.colors.backgroundDark};
          box-shadow: 0 4px 14px rgba(251, 191, 36, 0.3);
          
          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(251, 191, 36, 0.4);
          }

          &:active:not(:disabled) {
            transform: translateY(0);
          }
          
          &:disabled {
            background: rgba(251, 191, 36, 0.2);
            color: rgba(251, 191, 36, 0.5);
            cursor: not-allowed;
            box-shadow: none;
          }
        `;
      case 'primary':
        return `
          background: ${theme.gradients.primary};
          color: ${theme.colors.backgroundDark};
          box-shadow: 0 4px 14px rgba(0, 208, 192, 0.3);
          
          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(0, 208, 192, 0.4), 0 0 40px rgba(99, 102, 241, 0.2);
          }

          &:active:not(:disabled) {
            transform: translateY(0);
          }
          
          &:disabled {
            background: rgba(0, 208, 192, 0.15);
            color: rgba(0, 208, 192, 0.4);
            cursor: not-allowed;
            box-shadow: none;
          }
        `;
      case 'green':
        return `
          background: ${theme.gradients.success};
          color: white;
          box-shadow: 0 4px 14px rgba(34, 197, 94, 0.3);
          
          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
          }

          &:active:not(:disabled) {
            transform: translateY(0);
          }
          
          &:disabled {
            background: rgba(34, 197, 94, 0.2);
            color: rgba(34, 197, 94, 0.5);
            cursor: not-allowed;
            box-shadow: none;
          }
        `;
      default:
        return `
          background: ${theme.colors.glass};
          color: ${theme.colors.text};
          border: 1px solid ${theme.colors.glassBorder};
          
          &:hover:not(:disabled) {
            background: ${theme.colors.glassHover};
            border-color: rgba(0, 208, 192, 0.3);
            transform: translateY(-2px);
          }

          &:active:not(:disabled) {
            transform: translateY(0);
          }
          
          &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
          }
        `;
    }
  }}
`;

export const BaseButton: React.FC<BaseButtonProps> = ({
  children,
  variant = 'default',
  fullWidth = false,
  icon,
  ...props
}) => {
  return (
    <StyledButton
      $variant={variant}
      $fullWidth={fullWidth}
      {...props}
    >
      {icon}
      {children}
    </StyledButton>
  );
};
